/**
 *
 * EditModal
 *
 */

import React, { useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import produce from 'immer';
import { FormattedMessage } from 'react-intl';
import { last, uniqueId } from 'lodash-es';
import { Button, Form, Modal, Card } from 'react-bootstrap';
import IconButton from 'components/interaction/IconButton/index.jsx';
import PrimaryAction from 'components/interaction/PrimaryAction/index.jsx';
import messages from './messages.js';

const ModalBody = styled(Modal.Body)`
  max-height: calc(100vh - 180px);
  overflow-y: auto;
`;

function init({ meetings, parentIds }) {
  const result = { list: [], parentIds };
  meetings.forEach((mt) => {
    result.list.push(mt.id);
    result[mt.id] = new Set(mt.parentIds);
  });
  return result;
}

const reducer = produce((draft, action) => {
  const { type } = action;
  const { parentId, meetingId } = action;
  switch (type) {
    case 'change':
      if (draft[meetingId].has(parentId)) {
        draft[meetingId].delete(parentId);
      } else {
        draft[meetingId].add(parentId);
      }
      break;
    case 'add': {
      // If some parent is not included yet, add it to the new meeting and return
      const unused = draft.parentIds.filter((pid) => !draft.list.some((mid) => draft[mid].has(pid)));
      if (unused.length > 0) {
        draft[meetingId] = new Set(unused);
        draft.list.push(meetingId);
        break;
      }
      // Find last meeting with at least two parents
      const dupId = draft.list.slice(0).reverse().find((id) => draft[id].size > 1);
      const dupMeeting = draft[dupId];
      const lastParentId = last(Array.from(dupMeeting));
      dupMeeting.delete(lastParentId);
      draft[meetingId] = new Set([lastParentId]);
      draft.list.push(meetingId);
      break;
    }
    case 'delete':
      draft.list = draft.list.filter((id) => id !== meetingId);
      delete draft[meetingId];
      break;
    default:
  }
});

function EditModal(props) {
  const { value = {}, onChange, onHide } = props;
  const { student, meetings } = value;
  const parentIds = student?.connections.list;

  const [state, dispatch] = useReducer(reducer, { meetings, parentIds }, init);
  const [submitted, setSubmitted] = useState(false);

  const changeMeeting = (parentId, meetingId) => dispatch({ type: 'change', parentId, meetingId });
  const addMeeting = () => dispatch({ type: 'add', meetingId: uniqueId('tmp_') });
  const dropMeeting = (meetingId) => dispatch({ type: 'delete', meetingId });

  function handleSubmit(ev) {
    ev.preventDefault();
    ev.stopPropagation();
    setSubmitted(true);
    onChange(state.list.map((id) => ({ id, studentId: student.id, parentIds: Array.from(state[id]) })));
    onHide();
  }

  return (
    <Modal show onHide={onHide}>
      <form onSubmit={handleSubmit}>
        <Modal.Header className="border-bottom-0">
          <Modal.Title>
            <FormattedMessage {...messages.header} values={{ name: student.displayName }} />
          </Modal.Title>
        </Modal.Header>
        <ModalBody>
          {state.list.map((meetingId, index) => (
            <Card key={meetingId} className="border">
              <Card.Body>
                {state.list.length > 1
                && <IconButton variant="link" className="float-end" icon="ss-trash fs-3 text-danger" onClick={() => dropMeeting(meetingId)} />}
                <h4 className="mt-0">
                  <FormattedMessage {...messages.meeting} />
                  {' '}
                  {state.list.length > 1 && index + 1}
                </h4>
                <p><FormattedMessage {...messages.intro} /></p>
                {parentIds.map((parentId) => (
                  <Form.Check
                    type="checkbox"
                    key={parentId}
                    checked={state[meetingId].has(parentId)}
                    onChange={() => changeMeeting(parentId, meetingId)}
                    label={student.connections[parentId].displayName}
                  />
                ))}
              </Card.Body>
            </Card>
          ))}
          {parentIds.length > state.list.length && (
            <Button variant="link" onClick={addMeeting}><FormattedMessage {...messages.extra} /></Button>
          )}
        </ModalBody>
        <Modal.Footer>
          <PrimaryAction onCancel={onHide} submitted={submitted} />
        </Modal.Footer>
      </form>
    </Modal>
  );
}

EditModal.propTypes = {
  onHide: PropTypes.func,
  value: PropTypes.object,
  onChange: PropTypes.func,
};

export default EditModal;
